import type { ComponentProps, HTMLAttributes, ReactNode } from 'react'
import { cn } from './cn.js'

/**
 * Edge-to-edge list with inset hairline dividers — the density pattern from
 * native settings screens. Rows own their own separator so a list can be
 * assembled from heterogeneous children without wrapper elements.
 */
export function List({ className, ...props }: ComponentProps<'div'>) {
  return <div className={cn('flex flex-col', className)} {...props} />
}

/**
 * `HTMLAttributes<HTMLElement>` rather than the div's own props: a row renders
 * as a `<button>` when it is tappable, and the caller's extra attributes have
 * to be valid on either element.
 */
export interface ListRowProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  title: ReactNode
  subtitle?: ReactNode
  /** Right-aligned value; rendered with tabular figures so columns line up. */
  value?: ReactNode
  meta?: ReactNode
  leading?: ReactNode
  /**
   * Control at the far right — a stepper, a switch. A row carrying one is never
   * a button: the control is the target, and nesting it inside one would give
   * the row two overlapping hit areas and invalid markup.
   */
  accessory?: ReactNode
  onActivate?: () => void
  /** Suppress the divider on the last row of a group. */
  last?: boolean
  /** Let a long subtitle wrap onto a second line instead of truncating to one. */
  wrapSubtitle?: boolean
}

export function ListRow({
  title,
  subtitle,
  value,
  meta,
  leading,
  accessory,
  onActivate,
  last = false,
  wrapSubtitle = false,
  className,
  ...props
}: ListRowProps) {
  const rowClass = cn(
    // select-none: a long-press on a row should feel like a press, not start
    // selecting the title text.
    'relative flex min-h-11 select-none items-center gap-3 px-4 py-2 text-left',
    !last &&
      "after:absolute after:bottom-0 after:left-4 after:right-0 after:h-px after:bg-line after:content-['']",
    onActivate &&
      'cursor-pointer transition-colors duration-[140ms] hover:bg-raised active:bg-raised',
    className,
  )

  const body = (
    <>
      {leading}
      <div className="min-w-0 flex-1">
        <div className="truncate text-base text-ink">{title}</div>
        {subtitle != null && (
          <div className={cn('text-sm text-mute', wrapSubtitle ? 'line-clamp-2' : 'truncate')}>
            {subtitle}
          </div>
        )}
      </div>
      {(value != null || meta != null) && (
        <div className="shrink-0 text-right">
          {value != null && <div className="tnum text-base text-ink">{value}</div>}
          {meta != null && <div className="tnum text-sm text-mute">{meta}</div>}
        </div>
      )}
    </>
  )

  if (accessory != null) {
    return (
      <div className={rowClass} {...props}>
        {body}
        <div className="shrink-0">{accessory}</div>
      </div>
    )
  }

  // A tappable row is a real button rather than a div wearing role="button": it
  // gets Enter/Space, focus order and the accessibility tree for free, and a
  // conditional role would only be interactive-looking to sighted users.
  if (onActivate) {
    return (
      <button type="button" className={cn(rowClass, 'w-full')} onClick={onActivate} {...props}>
        {body}
      </button>
    )
  }

  return (
    <div className={rowClass} {...props}>
      {body}
    </div>
  )
}

/** Small uppercase caption that introduces a group of rows. */
export function SectionLabel({ className, children, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'px-4 pb-1 pt-5 text-xs font-semibold uppercase tracking-[0.06em] text-mute',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  )
}

export function Card({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn('rounded-[var(--radius-card)] border border-line bg-surface', className)}
      {...props}
    />
  )
}

export function EmptyState({
  title,
  hint,
  action,
  motif,
  className,
}: {
  title: string
  hint?: string
  action?: ReactNode
  /**
   * A small drawing above the words.
   *
   * Optional, and meant to stay that way: this used to be three stacked
   * paragraphs and nothing else, which is right for a list that is empty
   * because a filter matched nothing and too bare for a screen somebody is
   * seeing on their first day. One stroke of line art, in the same vocabulary
   * as the first meeting's motifs — never an illustration, and never on a
   * surface where the emptiness is routine.
   */
  motif?: ReactNode
  className?: string
}) {
  return (
    <div className={cn('flex flex-col items-center gap-2 px-6 py-10 text-center', className)}>
      {motif != null && <div className="mb-1">{motif}</div>}
      <p className="text-base text-dim">{title}</p>
      {hint != null && <p className="max-w-xs text-sm text-mute">{hint}</p>}
      {action}
    </div>
  )
}
